import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import MermaidBlock from './MermaidBlock';
import './Mentions.css';
import { mediaUrl } from '../media';
import UserAvatar from './UserAvatar';

/*
 * ──────────────────────────────────────────────────────────────────────────────
 * Mentions
 *
 * Storage format: [@DisplayName](mention://USER_ID)
 *   - Parses as a standard markdown link, so remark-gfm handles it natively.
 *   - The `mention://` URL scheme is unambiguous; a custom <a> renderer detects
 *     it and renders a pill instead.
 *   - The user ID stays in the stored content so the backend can parse it for
 *     notifications without a lookup.
 *
 * This module exports:
 *   - MentionTextarea — drop-in wrapper around <textarea> with @-dropdown
 *   - MentionInput    — drop-in wrapper around <input type="text"> (single line)
 *   - mentionMarkdownComponents — spread into <ReactMarkdown components={...}>
 *   - extractMentionedUserIds(text) — mirrors backend/utils/mentions.js
 * ──────────────────────────────────────────────────────────────────────────────
 */

// Canonical mention pattern. MUST stay identical to MENTION_LINK_RE in
// backend/utils/mentions.js — frontend pills and backend notification
// targets are only guaranteed to agree while these two match.
//   - display name: no ']' and no newline (it is presentation only, never parsed)
//   - id: a bare 24-hex ObjectId, so `mention://` links with junk ids are ignored
const MENTION_REGEX_SOURCE = /\[@([^\]\n]+)\]\(mention:\/\/([0-9a-fA-F]{24})\)/;

// Walk back from the caret to see whether we're inside a live "@query" token.
// Returns { start, query } or null.
function findActiveMention(text, caret) {
  let i = caret - 1;
  while (i >= 0) {
    const c = text[i];
    if (c === '@') {
      // @ must be at start of text OR preceded by whitespace (skip emails etc.)
      if (i === 0 || /\s/.test(text[i - 1])) {
        const query = text.substring(i + 1, caret);
        if (!/\s/.test(query)) return { start: i, query };
      }
      return null;
    }
    if (/\s/.test(c)) return null; // whitespace breaks the token
    i--;
  }
  return null;
}

function filterMembers(members, query) {
  if (!members) return [];
  const max = 8;
  if (!query) return members.slice(0, max);
  const q = query.toLowerCase();
  return members
    .filter(m => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q))
    .slice(0, max);
}


// ──────────────────────────────────────────────────────────────────────────
// Caret geometry
//
// The dropdown is portalled to <body> with position:fixed so no ancestor's
// overflow or stacking context can clip it (FolioEditor scrolls its body;
// TaskModal is itself a z-indexed overlay). That means every coordinate
// below must be in VIEWPORT space.
//
// Browsers expose no caret rect for <textarea>/<input>, so we measure with a
// mirror div: an off-screen element styled identically to the field, filled
// with the text up to the caret. The offset of a marker span appended at that
// point is the caret's offset inside the field; add the field's viewport
// origin and subtract its scroll to land in viewport space.
// ──────────────────────────────────────────────────────────────────────────

// Style properties that affect text layout and therefore must be mirrored
// exactly, or the measured caret drifts from the real one.
const MIRRORED_STYLES = [
  'boxSizing', 'width',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'lineHeight', 'textTransform', 'textIndent',
  'wordSpacing', 'tabSize',
];

/**
 * Viewport coordinates of the caret in `field`, plus the line height so the
 * caller can offset below the current line rather than over it.
 * Returns null if the field is gone.
 */
function getCaretViewportRect(field) {
  const rect = field.getBoundingClientRect();
  const computed = window.getComputedStyle(field);
  const isInput = field.tagName === 'INPUT';

  const mirror = document.createElement('div');
  for (const prop of MIRRORED_STYLES) mirror.style[prop] = computed[prop];

  // Take the field out of flow and hide it, but keep it laid out so
  // offsetTop/offsetLeft are real.
  mirror.style.position = 'absolute';
  mirror.style.top = '0';
  mirror.style.left = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.pointerEvents = 'none';
  mirror.style.overflow = 'hidden';
  // A single-line input never wraps; a textarea wraps exactly like this.
  mirror.style.whiteSpace = isInput ? 'pre' : 'pre-wrap';
  mirror.style.wordWrap = isInput ? 'normal' : 'break-word';
  if (!isInput) mirror.style.height = 'auto';

  const caret = field.selectionStart ?? 0;
  mirror.textContent = field.value.substring(0, caret);

  // Marker sits exactly where the caret is. It needs non-empty content to
  // get a box; a zero-width space measures without adding visible width.
  const marker = document.createElement('span');
  marker.textContent = field.value.substring(caret) || '\u200b';
  mirror.appendChild(marker);

  const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2 || 16;

  // finally: the mirror must never survive a throw, or hidden divs pile up
  // on <body> for the life of the page.
  let offsetTop, offsetLeft;
  document.body.appendChild(mirror);
  try {
    offsetTop = marker.offsetTop;
    offsetLeft = marker.offsetLeft;
  } finally {
    document.body.removeChild(mirror);
  }

  return {
    top: rect.top + offsetTop - field.scrollTop,
    left: rect.left + offsetLeft - field.scrollLeft,
    lineHeight,
    fieldRect: rect,
  };
}

const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_WIDTH = 300;
const ROW_HEIGHT = 46;        // one .mention-dropdown-item, incl. padding
const VIEWPORT_MARGIN = 8;    // never touch the viewport edge
const GAP = 4;                // breathing room between caret line and panel

/**
 * Turn a caret position into a fixed-position style for the dropdown:
 * anchored at the caret, flipped above the line when there isn't room
 * below, and clamped so it never overflows the viewport horizontally.
 */
function dropdownStyleForCaret(field, itemCount) {
  const caretRect = getCaretViewportRect(field);
  if (!caretRect) return null;

  const { top, left, lineHeight } = caretRect;
  const width = Math.min(DROPDOWN_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const height = Math.min(DROPDOWN_MAX_HEIGHT, itemCount * ROW_HEIGHT + 8);

  // Clamp horizontally: prefer the caret's column, but pull left rather
  // than let the panel run off the right edge.
  const clampedLeft = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN)
  );

  // Flip up when the space below the caret line can't hold the panel.
  const spaceBelow = window.innerHeight - (top + lineHeight);
  const openUpward = spaceBelow < height + GAP && top > spaceBelow;

  const style = {
    position: 'fixed',
    left: clampedLeft,
    width,
    maxHeight: DROPDOWN_MAX_HEIGHT,
  };
  if (openUpward) {
    style.bottom = Math.max(VIEWPORT_MARGIN, window.innerHeight - top + GAP);
  } else {
    style.top = Math.min(
      top + lineHeight + GAP,
      window.innerHeight - height - VIEWPORT_MARGIN
    );
  }
  return style;
}

function MentionDropdown({ members, activeIndex, onSelect, onHover, style }) {
  return (
    <div className="mention-dropdown" style={style} role="listbox">
      {members.map((m, idx) => (
        <div
          key={m._id}
          role="option"
          aria-selected={idx === activeIndex}
          className={`mention-dropdown-item${idx === activeIndex ? ' active' : ''}`}
          // onMouseDown instead of onClick: fires before the textarea's blur
          // event, so the field keeps focus when we insert.
          onMouseDown={(e) => { e.preventDefault(); onSelect(m); }}
          onMouseEnter={() => onHover(idx)}
        >
          {/* WR-018 §2: through UserAvatar so a failed load degrades to
              initials rather than painting alt={m.name} in the circle. */}
          <UserAvatar user={m} className="mention-dropdown-avatar" />
          <div className="mention-dropdown-text">
            <div className="mention-dropdown-name">{m.name}</div>
            {m.email && <div className="mention-dropdown-email">{m.email}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Shared mention state + handlers. Wires up to either an <input> or <textarea>
 * via a ref. Returns handlers plus the portal-mounted dropdown node.
 */
function useMentions({ fieldRef, value, onChange, members }) {
  const [open, setOpen] = useState(false);
  const [match, setMatch] = useState(null); // { start, query }
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const filtered = filterMembers(members, match?.query ?? '');

  // Recompute mention state from the current field contents + caret.
  const sync = useCallback(() => {
    const field = fieldRef.current;
    if (!field) return;
    const caret = field.selectionStart ?? 0;
    const m = findActiveMention(value, caret);
    if (m) {
      setMatch(m);
      setOpen(true);
      // Height depends on how many rows will render, which decides whether
      // the panel has to flip above the caret line.
      const count = Math.max(filterMembers(members, m.query).length, 1);
      const style = dropdownStyleForCaret(field, count);
      if (style) setDropdownStyle(style);
    } else {
      setOpen(false);
      setMatch(null);
    }
  }, [value, fieldRef, members]);

  // Re-sync when value changes (typing, paste, programmatic change).
  useEffect(() => { sync(); }, [sync]);

  // Reset active row when the filtered list changes shape.
  useEffect(() => { setActiveIndex(0); }, [match?.query, filtered.length]);

  // Close on click outside the field AND outside the dropdown.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const t = e.target;
      if (fieldRef.current?.contains(t)) return;
      if (t.closest?.('.mention-dropdown')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, fieldRef]);

  // Keep the panel glued to the caret while things move underneath it.
  // Capture phase matters: scrolling happens on ancestor containers (the
  // folio body, the task modal), not on window, and those events do not
  // bubble — only the capture pass sees them.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const field = fieldRef.current;
      if (!field) return;
      const style = dropdownStyleForCaret(field, Math.max(filtered.length, 1));
      if (style) setDropdownStyle(style);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, filtered.length, fieldRef]);

  const selectMember = (member) => {
    if (!match) return;
    const field = fieldRef.current;
    if (!field) return;
    const caret = field.selectionStart ?? value.length;
    const safeName = String(member.name || '').replace(/[\]\[()]/g, '').trim() || 'user';
    const before = value.substring(0, match.start);
    const after = value.substring(caret);
    const insert = `[@${safeName}](mention://${member._id}) `;
    const newValue = before + insert + after;
    const newCaret = before.length + insert.length;

    onChange(newValue);
    setOpen(false);
    setMatch(null);

    // Restore the caret position after React commits the new value.
    setTimeout(() => {
      if (fieldRef.current) {
        fieldRef.current.focus();
        fieldRef.current.setSelectionRange(newCaret, newCaret);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectMember(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Arrow-key navigation / clicks move the caret without changing value, so
  // we need these extra triggers for sync().
  const handleKeyUp = () => sync();
  const handleClick = () => sync();

  const dropdownNode = open && filtered.length > 0
    ? createPortal(
        <MentionDropdown
          members={filtered}
          activeIndex={activeIndex}
          onSelect={selectMember}
          onHover={setActiveIndex}
          style={dropdownStyle}
        />,
        document.body
      )
    : null;

  return { handleKeyDown, handleKeyUp, handleClick, dropdownNode };
}

// ──────────────────────────────────────────────────────────────────────────
// Public components
// ──────────────────────────────────────────────────────────────────────────

export function MentionTextarea({
  value, onChange, members,
  onKeyDown: userOnKeyDown, onKeyUp: userOnKeyUp, onClick: userOnClick,
  inputRef,
  ...rest
}) {
  const fieldRef = useRef(null);
  const { handleKeyDown, handleKeyUp, handleClick, dropdownNode } = useMentions({
    fieldRef, value, onChange, members,
  });

  // If caller passed an external ref, forward it.
  useEffect(() => {
    if (!inputRef) return;
    if (typeof inputRef === 'function') inputRef(fieldRef.current);
    else inputRef.current = fieldRef.current;
  });

  return (
    <>
      <textarea
        ref={fieldRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { handleKeyDown(e); if (!e.defaultPrevented) userOnKeyDown?.(e); }}
        onKeyUp={(e) => { handleKeyUp(); userOnKeyUp?.(e); }}
        onClick={(e) => { handleClick(); userOnClick?.(e); }}
        {...rest}
      />
      {dropdownNode}
    </>
  );
}

export function MentionInput({
  value, onChange, members,
  onKeyDown: userOnKeyDown, onKeyUp: userOnKeyUp, onClick: userOnClick,
  inputRef,
  ...rest
}) {
  const fieldRef = useRef(null);
  const { handleKeyDown, handleKeyUp, handleClick, dropdownNode } = useMentions({
    fieldRef, value, onChange, members,
  });

  useEffect(() => {
    if (!inputRef) return;
    if (typeof inputRef === 'function') inputRef(fieldRef.current);
    else inputRef.current = fieldRef.current;
  });

  return (
    <>
      <input
        ref={fieldRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { handleKeyDown(e); if (!e.defaultPrevented) userOnKeyDown?.(e); }}
        onKeyUp={(e) => { handleKeyUp(); userOnKeyUp?.(e); }}
        onClick={(e) => { handleClick(); userOnClick?.(e); }}
        {...rest}
      />
      {dropdownNode}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ReactMarkdown custom renderers
// ──────────────────────────────────────────────────────────────────────────

function MentionPill({ href, children }) {
  const userId = href.replace(/^mention:\/\//, '');
  return (
    <span className="mention-pill" data-user-id={userId}>
      {children}
    </span>
  );
}

/**
 * Spread into <ReactMarkdown components={...}>. Overrides the <a> renderer so
 * mention-scheme links become pills, and intercepts ```mermaid``` fenced code
 * blocks so they render as diagrams. All other links / code blocks get the
 * react-markdown defaults.
 */
export const mentionMarkdownComponents = {
  a: ({ href, children, ...rest }) => {
    if (typeof href === 'string' && href.startsWith('mention://')) {
      return <MentionPill href={href}>{children}</MentionPill>;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  },
  // Uploaded images embedded in Markdown (folio content, task descriptions)
  // point at /uploads, which is no longer public — sign them (WR-014).
  img: ({ src, alt, ...rest }) => (
    <img src={mediaUrl(src)} alt={alt || ''} {...rest} />
  ),
  code: ({ className, children, ...rest }) => {
    const match = /language-(\w+)/.exec(className || '');
    const lang  = match && match[1];
    if (lang === 'mermaid') {
      const source = String(children || '').replace(/\n$/, '');
      return <MermaidBlock chart={source} />;
    }
    return <code className={className} {...rest}>{children}</code>;
  },
};

// ──────────────────────────────────────────────────────────────────────────
// Helpers (exported for the upcoming notifications work)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Return a deduplicated list of user IDs mentioned in the given text.
 * Safe to call with null/undefined.
 *
 * Mirrors extractMentionedUserIds() in backend/utils/mentions.js, including
 * the code-span exclusion below. The backend copy is authoritative for who
 * actually gets notified; this one exists so the UI can reason about the
 * same set without a round-trip.
 */
export function extractMentionedUserIds(text) {
  if (!text || typeof text !== 'string') return [];
  // Markdown creates no links inside code, so a mention written in a fenced
  // block or an inline span renders as literal text — no pill, and no
  // notification. Strip code first so this helper agrees.
  const source = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/~~~[\s\S]*?~~~/g, ' ')
    .replace(/`[^`\n]*`/g, ' ');
  const ids = new Set();
  const re = new RegExp(MENTION_REGEX_SOURCE.source, 'g');
  let m;
  while ((m = re.exec(source)) !== null) ids.add(m[2]);
  return Array.from(ids);
}
